/**
 * Mobile Product Carousel (native scroll-snap locked to 1 image per swipe)
 * - Activates only under 750px
 * - Full-bleed, one image per view, dots inside
 * - No external libraries, native scroll-snap handles momentum
 */
(function () {
  var mql = window.matchMedia('(max-width: 749px)');
  var productRoot;

  var mediaWrapper;
  var mediaList;
  var slides;
  var dotsEl;
  var initialized = false;
  var currentIndex = 0;

  // Drag-to-scroll state
  var isDragging = false;
  var startX = 0;
  var scrollStart = 0;

  function setAria() {
    mediaWrapper.setAttribute('role', 'region');
    mediaWrapper.setAttribute('aria-label', 'Product media gallery');
    mediaWrapper.setAttribute('aria-roledescription', 'carousel');
  }

  function buildDots() {
    if (dotsEl) dotsEl.remove();

    dotsEl = document.createElement('div');
    dotsEl.className = 'mobi-carousel__dots';
    dotsEl.setAttribute('aria-label', 'Slide pagination');
    dotsEl.setAttribute('role', 'tablist');

    // Only build dots for visible slides (not hidden by variant filtering)
    var visibleSlides = slides.filter(function (s) {
      return !s.classList.contains('hidden');
    });

    visibleSlides.forEach(function (_s, i) {
      var b = document.createElement('button');
      b.type = 'button';
      b.setAttribute('role', 'tab');
      b.setAttribute('aria-label', 'Go to slide ' + (i + 1) + ' of ' + visibleSlides.length);
      b.dataset.index = String(i);
      if (i === currentIndex) b.setAttribute('aria-current', 'true');
      dotsEl.appendChild(b);
    });

    if (!mediaWrapper) return;

    mediaWrapper.appendChild(dotsEl);

    dotsEl.addEventListener('click', function (e) {
      var t = e.target;
      if (!(t instanceof HTMLElement)) return;
      var index = t.dataset && t.dataset.index ? parseInt(t.dataset.index, 10) : NaN;
      if (!isNaN(index) && index >= 0 && index < visibleSlides.length) {
        goToVisible(index);
      }
    });
  }

  function updateDotsActive() {
    if (!dotsEl) return;
    var children = dotsEl.children;
    for (var i = 0; i < children.length; i++) {
      var el = children[i];
      if (!(el instanceof HTMLElement)) continue;
      if (i === currentIndex) el.setAttribute('aria-current', 'true');
      else el.removeAttribute('aria-current');
    }
  }

  function resetGalleryVideos(exceptSlide) {
    // Tap-to-play deferred videos have no pause UI once playing. Reset
    // off-screen slides to the poster. Loop clips are not deferred-media;
    // product-gallery-clips.js pauses those.
    if (!slides || !slides.length) return;
    slides.forEach(function (slide) {
      if (exceptSlide && slide === exceptSlide) return;
      var deferred = slide.querySelector('.media-type-video deferred-media');
      if (deferred && typeof deferred.reset === 'function') deferred.reset();
    });
  }

  function goToVisible(index) {
    // Navigate to a visible slide by its index among visible slides
    var visibleSlides = slides.filter(function (s) {
      return !s.classList.contains('hidden');
    });
    if (index < 0 || index >= visibleSlides.length) return;
    visibleSlides[index].scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
    currentIndex = index;
    updateDotsActive();
    resetGalleryVideos(visibleSlides[currentIndex]);
  }

  function updateCurrentFromScroll() {
    // Determine which visible slide is currently in view using getBoundingClientRect
    var visibleSlides = slides.filter(function (s) {
      return !s.classList.contains('hidden');
    });

    if (visibleSlides.length === 0) return;

    // Get the scroll container's position
    var containerRect = mediaList.getBoundingClientRect();
    var containerLeft = containerRect.left;

    // Find which visible slide is closest to the container's left edge
    var closestIndex = 0;
    var minDistance = Infinity;

    visibleSlides.forEach(function (slide, index) {
      var slideRect = slide.getBoundingClientRect();
      var distance = Math.abs(slideRect.left - containerLeft);

      if (distance < minDistance) {
        minDistance = distance;
        closestIndex = index;
      }
    });

    if (closestIndex !== currentIndex && closestIndex >= 0 && closestIndex < visibleSlides.length) {
      currentIndex = closestIndex;
      updateDotsActive();
      resetGalleryVideos(visibleSlides[currentIndex]);
    }
  }

  var ticking = false;
  function onScroll() {
    if (!ticking) {
      window.requestAnimationFrame(function () {
        updateCurrentFromScroll();
        ticking = false;
      });
      ticking = true;
    }
  }

  // Drag-to-scroll handlers
  function onMouseDown(e) {
    isDragging = true;
    startX = e.pageX - mediaList.offsetLeft;
    scrollStart = mediaList.scrollLeft;
    mediaList.style.cursor = 'grabbing';
    mediaList.style.userSelect = 'none';
  }

  function onMouseMove(e) {
    if (!isDragging) return;
    e.preventDefault();
    var x = e.pageX - mediaList.offsetLeft;
    var walk = (x - startX) * 1.5; // Multiply for faster scroll
    mediaList.scrollLeft = scrollStart - walk;
  }

  function onMouseUp() {
    isDragging = false;
    mediaList.style.cursor = 'grab';
    mediaList.style.userSelect = '';
  }

  function onMouseLeave() {
    if (isDragging) {
      isDragging = false;
      mediaList.style.cursor = 'grab';
      mediaList.style.userSelect = '';
    }
  }

  function init() {
    // Re-query productRoot in case the page was updated
    if (!productRoot) {
      productRoot = document.querySelector('product-info');
    }
    if (!productRoot) return;

    mediaWrapper = productRoot.querySelector('.product__media-wrapper');
    mediaList = productRoot.querySelector('.product__media-list');
    if (!mediaWrapper || !mediaList) return;

    slides = Array.prototype.slice.call(mediaList.querySelectorAll('.product__media-item'));
    if (!slides.length) return;

    // If already initialized, refresh state and return
    if (initialized) {
      // Update slides in case they changed (variant switch)
      slides = Array.prototype.slice.call(mediaList.querySelectorAll('.product__media-item'));
      // Rebuild dots if missing or if slide count changed
      if (!dotsEl || !mediaWrapper.contains(dotsEl)) {
        buildDots();
      }
      return;
    }

    // Add classes
    mediaWrapper.classList.add('mobi-carousel');
    document.body.classList.add('mobi-carousel-active');

    setAria();
    buildDots();

    // Ensure starting at the very first slide to avoid any initial peek
    try {
      mediaList.scrollTo({ left: 0, behavior: 'auto' });
    } catch (_e) {
      mediaList.scrollLeft = 0;
    }

    mediaList.addEventListener('scroll', onScroll, { passive: true });

    // Add drag-to-scroll event listeners
    mediaList.addEventListener('mousedown', onMouseDown);
    mediaList.addEventListener('mousemove', onMouseMove);
    mediaList.addEventListener('mouseup', onMouseUp);
    mediaList.addEventListener('mouseleave', onMouseLeave);

    // Set initial cursor style
    mediaList.style.cursor = 'grab';

    // Ensure first slide is in view
    currentIndex = 0;
    updateDotsActive();
    initialized = true;
  }

  function destroy() {
    if (!initialized) return;

    mediaWrapper.classList.remove('mobi-carousel');
    document.body.classList.remove('mobi-carousel-active');
    mediaWrapper.removeAttribute('role');
    mediaWrapper.removeAttribute('aria-label');
    mediaWrapper.removeAttribute('aria-roledescription');
    if (dotsEl) dotsEl.remove();
    mediaList.removeEventListener('scroll', onScroll);

    // Remove drag-to-scroll event listeners
    mediaList.removeEventListener('mousedown', onMouseDown);
    mediaList.removeEventListener('mousemove', onMouseMove);
    mediaList.removeEventListener('mouseup', onMouseUp);
    mediaList.removeEventListener('mouseleave', onMouseLeave);

    // Reset cursor style
    mediaList.style.cursor = '';

    initialized = false;
  }

  // Handle responsive initialization and changes
  function handleResize() {
    if (mql.matches) {
      init();
    } else {
      destroy();
    }
  }

  // Initial setup when DOM is ready
  function setup() {
    if (!productRoot) {
      productRoot = document.querySelector('product-info');
    }
    if (!productRoot) return;

    // Initial check
    handleResize();

    // Listen to window resize events (most reliable for viewport changes)
    var resizeTimeout;
    window.addEventListener('resize', function () {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(function () {
        handleResize();
      }, 150); // Debounce to avoid excessive calls
    });

    // Also listen to media query changes as a fallback
    mql.addEventListener('change', handleResize);
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', setup);
  } else {
    setup();
  }

  // Reinit when Shopify updates the product (variant change, etc.)
  document.addEventListener('shopify:section:load', function () {
    // Destroy and re-initialize to pick up DOM changes
    destroy();
    handleResize();
  });

  // Listen for multi-variant-images variant changes
  document.addEventListener('variant:changed', function () {
    if (!mql.matches || !initialized) return;

    // Re-query slides to get updated visibility
    slides = Array.prototype.slice.call(mediaList.querySelectorAll('.product__media-item'));

    // Reset to first visible slide
    currentIndex = 0;
    var firstVisible = slides.find(function (s) {
      return !s.classList.contains('hidden');
    });
    if (firstVisible) {
      try {
        firstVisible.scrollIntoView({ behavior: 'auto', block: 'nearest', inline: 'start' });
      } catch (_e) {
        // Fallback if scrollIntoView fails
      }
    }

    // Rebuild dots to match visible slides
    buildDots();
  });
})();
